/**
 * blog-pillar.js — Blog 孕周支柱页交互
 * 用途: sections/blog-pillar-week-grid.liquid 的胶囊 Tabs 切换（Progressive Enhancement）
 * 依赖: 无（原生 JS，无第三方库）
 * 用法: <div data-blog-pillar-week-grid> ... <button data-blog-pillar-tab="1"> ... <div data-blog-pillar-panel="1">
 *
 * 设计原则:
 *   1. 无 JS 时三个分组面板全部平铺展示，42 周入口对爬虫与用户完全可见；
 *      脚本执行成功后才写入 data-blog-pillar-ready，由 CSS 接管「只显示当前分组」。
 *   2. 遵循 WAI-ARIA Tabs 模式：role/aria-selected/tabindex + 方向键导航。
 *   3. 移动端（≤767px）同样支持胶囊 Tabs 切换；
 *      「展开更多」按钮仅在移动端生效（每阶段默认最多 6 张周卡）。
 */
;(function () {
  'use strict'

  /**
   * 移动端每个阶段默认最多展示的周卡数量
   * 与 assets/blog-pillar-week-grid.css 中的 :nth-child(n + 7) 保持一致，改动需同时修改两处
   */
  var MOBILE_LIMIT = 6

  /**
   * 激活指定分组
   * @param {HTMLElement} root - [data-blog-pillar-week-grid] 根元素
   * @param {string} group - 分组号（"1" | "2" | "3"）
   */
  function activate(root, group) {
    const panels = root.querySelectorAll('[data-blog-pillar-panel]')
    const tabs = root.querySelectorAll('[data-blog-pillar-tab]')

    // 面板：仅当前分组带 data-blog-pillar-active（CSS 据此显隐）
    for (let i = 0; i < panels.length; i++) {
      if (panels[i].getAttribute('data-blog-pillar-panel') === group) {
        panels[i].setAttribute('data-blog-pillar-active', '')
      } else {
        panels[i].removeAttribute('data-blog-pillar-active')
      }
    }

    // Tab：同步 aria-selected 与 tabindex
    for (let j = 0; j < tabs.length; j++) {
      const on = tabs[j].getAttribute('data-blog-pillar-tab') === group
      tabs[j].setAttribute('aria-selected', on ? 'true' : 'false')
      tabs[j].tabIndex = on ? 0 : -1
      if (on) scrollTabIntoLeft(tabs[j])
    }
  }

  /**
   * 把选中 Tab 的左边缘对齐标签栏可视区左边缘（仅 H5，左右双向，尽头钳制）
   * @param {HTMLElement} tab - 选中的 tab 按钮
   */
  function scrollTabIntoLeft(tab) {
    if (!tab) return
    if (!window.matchMedia('(max-width: 768px)').matches) return
    const tablist = tab.closest('.blog-pillar-week-grid__tabs') || tab.parentElement
    if (!tablist) return
    const box = tablist.getBoundingClientRect()
    const rect = tab.getBoundingClientRect()
    const cs = window.getComputedStyle(tablist)
    const padLeft = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.borderLeftWidth) || 0)
    const delta = rect.left - (box.left + padLeft)
    if (Math.abs(delta) < 1) return
    const maxLeft = Math.max(0, tablist.scrollWidth - tablist.clientWidth)
    const target = Math.min(Math.max(0, tablist.scrollLeft + delta), maxLeft)
    if (Math.abs(target - tablist.scrollLeft) < 1) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    tablist.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' })
  }

  /**
   * 初始化单个区块的 Tabs
   * @param {HTMLElement} root - [data-blog-pillar-week-grid] 根元素
   */
  function initTabs(root) {
    if (root.hasAttribute('data-blog-pillar-ready')) return

    const tabs = root.querySelectorAll('[data-blog-pillar-tab]')
    const panels = root.querySelectorAll('[data-blog-pillar-panel]')
    // 缺少 tab 按钮（商家关闭了开关）或没有面板时不启用，保持全部平铺
    if (!tabs.length || !panels.length) return

    // 以第一个面板为初始分组，避免与 HTML 中的首屏状态不一致
    const firstGroup = panels[0].getAttribute('data-blog-pillar-panel')

    for (let i = 0; i < tabs.length; i++) {
      ;(function (tab, index) {
        tab.addEventListener('click', function () {
          activate(root, tab.getAttribute('data-blog-pillar-tab'))
        })

        // WAI-ARIA Tabs 键盘导航：左右方向键切换，Home/End 跳首尾
        tab.addEventListener('keydown', function (event) {
          let next = null
          if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length]
          else if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length]
          else if (event.key === 'Home') next = tabs[0]
          else if (event.key === 'End') next = tabs[tabs.length - 1]
          if (!next) return

          event.preventDefault()
          activate(root, next.getAttribute('data-blog-pillar-tab'))
          next.focus()
        })
      })(tabs[i], i)
    }

    activate(root, firstGroup)
    // 最后才打标：CSS 从此接管显隐，实现无 JS 降级
    root.setAttribute('data-blog-pillar-ready', '')
  }

  /**
   * 初始化「展开更多 / 收起」按钮
   * 仅移动端生效：PC / 平板由 CSS 隐藏按钮且不裁剪列表
   * @param {HTMLElement} root - [data-blog-pillar-week-grid] 根元素
   */
  function initMore(root) {
    if (root.hasAttribute('data-blog-pillar-more-ready')) return

    const buttons = root.querySelectorAll('[data-blog-pillar-more]')
    if (!buttons.length) return

    let usable = 0

    for (let i = 0; i < buttons.length; i++) {
      ;(function (btn) {
        const panel = btn.closest('[data-blog-pillar-panel]')
        if (!panel) return

        const items = panel.querySelectorAll('.blog-pillar-week-grid__item')
        // 未超过上限时按钮没有意义，直接从 DOM 移除
        if (items.length <= MOBILE_LIMIT) {
          if (btn.parentNode) btn.parentNode.removeChild(btn)
          return
        }

        usable++

        const label = btn.querySelector('.dsm-btn__label')
        const moreText = btn.getAttribute('data-more-label') || 'Show More'
        const lessText = btn.getAttribute('data-less-label') || 'Show Less'

        btn.addEventListener('click', function () {
          const expanded = panel.hasAttribute('data-blog-pillar-expanded')
          if (expanded) {
            panel.removeAttribute('data-blog-pillar-expanded')
          } else {
            panel.setAttribute('data-blog-pillar-expanded', '')
          }
          btn.setAttribute('aria-expanded', expanded ? 'false' : 'true')
          if (label) label.textContent = expanded ? moreText : lessText
        })
      })(buttons[i])
    }

    // 有可用按钮才打标，CSS 据此在移动端裁剪列表并显示按钮（保证无 JS 时 42 周全可见）
    if (usable) root.setAttribute('data-blog-pillar-more-ready', '')
  }

  /**
   * 入口：初始化页面上所有孕周网格区块
   */
  function init() {
    const roots = document.querySelectorAll('[data-blog-pillar-week-grid]')
    for (let i = 0; i < roots.length; i++) {
      initTabs(roots[i])
      initMore(roots[i])
    }
  }

  // defer 脚本在 DOMContentLoaded 前执行；两种时机都兜住，避免主题编辑器二次注入时漏初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
